import { Component, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { NgModel } from '@angular/forms';
import { NgxGpAutocompleteDirective, NgxGpAutocompleteModule } from '@angular-magic/ngx-gp-autocomplete';
import { Store, createSelector } from '@ngrx/store';
import { isNil } from 'lodash';
import { MenuItem } from 'primeng/api';

import { animationFrameScheduler, debounceTime, filter, observeOn, tap } from 'rxjs';
import { ControlMetaDirective } from 'src/app/new-opportunity/control-meta.directive';
import { getSizeLabelForBuildingType } from 'src/app/shared/location-details-wizard/location-details-wizard.selectors';
import { BuildingType, buildingTypesWithAccess } from 'src/app/shared/location-details-wizard/location-details-wizard.state';
import { ordinalSuffix } from 'src/app/shared/location-details-wizard/location-details-wizard.utils';
import { createConfigSelector } from 'src/app/state/branding.store';
import { SubSink } from 'subsink';

import { FreyaCommonModule } from '../../../../freya-common/freya-common.module';
import { JOB_LOCATION_TYPES } from '../../../../global.constants';
import { LibModule } from '../../../../lib.ts/lib.module';
import { FreyaHelperService } from '../../../../services/freya-helper.service';
import { GoogleHelperService } from '../../../../services/google-helper.service';
import { JobManuallyAddLocationComponent } from '../../../job-create/job-manually-add-location/job-manually-add-location.component';
import {
  buildBuildingTypeOptions,
  buildAccessOptions,
  buildSizeOptions,
  buildElevatorTypeOptions,
} from '../../../job-locations-helpers';
import { selectJobInCurrentZone } from '../../../job-tool.reducer';
import { JobLocationsOverviewActions } from '../overview-locations.actions';
import { overviewLocationsFeature } from '../overview-locations.feature';
import { selectActiveJobLocation, selectActiveLocationEditor } from '../overview-locations.selectors';

@Component({
  selector: 'app-overview-location-editor',
  standalone: true,
  imports: [
    FreyaCommonModule,
    LibModule,
    NgxGpAutocompleteModule,
    JobManuallyAddLocationComponent,
    ControlMetaDirective,
  ],
  templateUrl: './overview-location-editor.component.html',
  styleUrl: './overview-location-editor.component.scss'
})
export class OverviewLocationEditorComponent implements OnDestroy, AfterViewInit {

  private _subs = new SubSink();

  @ViewChild(NgxGpAutocompleteDirective) addressAutocomplete: NgxGpAutocompleteDirective;
  @ViewChild('buildingTypeModel') buildingTypeModel: NgModel;
  @ViewChild('sizeModel') sizeModel: NgModel;
  @ViewChild('accessModel') accessModel: NgModel;

  public actions = JobLocationsOverviewActions;

  constructor(
    public store: Store,
    public googleHelper: GoogleHelperService,
    private freyaHelper: FreyaHelperService,
  ) {}

  viewModel$ = this.store.select(createSelector({
    locationEditor: selectActiveLocationEditor,
    buildingTypeOptions: createSelector(
      selectActiveLocationEditor,
      (editor) => buildBuildingTypeOptions(editor?.buildingType),
    ),
    sizeOptions: createSelector(
      selectActiveLocationEditor,
      (editor) => buildSizeOptions({
        buildingType: editor?.buildingType,
        currentValue: editor?.size,
      }),
    ),
    sizeLabel: createSelector(
      selectActiveLocationEditor,
      (editor) => getSizeLabelForBuildingType(editor?.buildingType),
    ),
    accessOptions: createSelector(
      selectActiveLocationEditor,
      (editor) => {
        if (!(buildingTypesWithAccess.includes(editor?.buildingType as BuildingType))) {
          return [];
        }

        return buildAccessOptions({
          buildingType: editor?.buildingType,
          currentValue: editor?.access,
        });
      },
    ),
    elevatorTypeOptions: createSelector(
      selectActiveLocationEditor,
      (editor) => {
        if (!(buildingTypesWithAccess.includes(editor?.buildingType as BuildingType))) {
          return [];
        }

        if (editor?.access !== 'elevator') {
          return [];
        }

        return buildElevatorTypeOptions(editor?.elevatorType);
      }
    ),
    jobLocation: selectActiveJobLocation,
    locationInvalid: createSelector(
      selectActiveLocationEditor,
      (editor) => !editor?.selectedLocation,
    ),
    openInGoogleMapsButton: createSelector(
      selectActiveLocationEditor,
      (editor) => {
        const disabled = !editor?.selectedLocation;
        return {
          disabled,
          tooltip: disabled ? 'Select a location to open in Google Maps' : 'Open in Google Maps',
        };
      },
    ),
    openInGoogleSearchButton: createSelector(
      selectActiveLocationEditor,
      (editor) => {
        const disabled = !editor?.selectedLocation;
        return {
          disabled,
          tooltip: disabled ? 'Select a location to search in Google' : 'Search in Google',
        };
      },
    ),
    openInZillowButton: createSelector(
      selectActiveLocationEditor,
      (editor) => {

        if (!editor?.selectedLocation) {
          return {
            disabled: true,
            tooltip: 'Select a location to open in Zillow',
          };
        }

        const hasCoordinates = Boolean(
          editor?.selectedLocation?.coordinates?.latitude &&
          editor?.selectedLocation?.coordinates?.longitude
        );

        return {
          disabled: !hasCoordinates,
          tooltip: hasCoordinates ? 'Open in Zillow' : 'We have no coordinates for this location',
        };
      },
    ),
    locationType: overviewLocationsFeature.selectActiveLocationType,
    openWizard: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      selectActiveLocationEditor,
      (locationType, editor) => {
        if (locationType !== 'start' && locationType !== 'end') return false;

        return !editor?.buildingType && !editor?.size && !editor?.access;
      },
    ),
    showDetails: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      (locationType) => locationType === 'start' || locationType === 'end',
    ),
    showAccessNotes: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      (locationType) => locationType === 'start' || locationType === 'end',
    ),
    showAccess: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      selectActiveLocationEditor,
      (
        locationType,
        editor,
      ) => (locationType === 'start' || locationType === 'end') && buildingTypesWithAccess.includes(editor?.buildingType as BuildingType),
    ),
    showElevatorType: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      selectActiveLocationEditor,
      (locationType, editor) => {
        if (locationType !== 'start' && locationType !== 'end') return false;

        if (!(buildingTypesWithAccess.includes(editor?.buildingType as BuildingType))) {
          return false;
        };

        return editor?.access === 'elevator' && editor?.buildingType !== 'Storage Unit';
      },
    ),
    showFloor: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      selectActiveLocationEditor,
      (locationType, editor) => {
        if (locationType !== 'start' && locationType !== 'end') return false;

        if (!(buildingTypesWithAccess.includes(editor?.buildingType as BuildingType))) {
          return false;
        };

        return [ 'stairs', 'elevator' ].includes(editor?.access);
      },
    ),
    floorSuffix: createSelector(
      selectActiveLocationEditor,
      (editor) => {

        if (!editor || isNil(editor.floor)) return;

        let suffix = `${ordinalSuffix(editor.floor)} floor`;

        if (editor.floor === 1) {
          suffix = `${suffix} (ground floor)`;
        }

        return suffix;
      }
    ),
    dwellingTypeMandatory: createSelector(
      createConfigSelector('JOBS_DWELLINGTYPEMANDATORY'),
      selectActiveLocationEditor,
      (config, editor) => Boolean(config && editor?.selectedLocation),
    ),
    dwellingTypeControlKey: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      (locationType) => `${locationType}DwellingType`,
    ),
    accessMandatory: createSelector(
      createConfigSelector('JOBS_ACCESSMANDATORY'),
      selectActiveLocationEditor,
      (config, editor) => Boolean(
        config
        && editor?.selectedLocation
        && buildingTypesWithAccess.includes(editor?.buildingType as BuildingType)
      ),
    ),
    accessControlKey: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      (locationType) => `${locationType}Access`,
    ),
    bedroomsMandatory: createSelector(
      createConfigSelector('JOBS_BEDROOMSMANDATORY'),
      selectActiveLocationEditor,
      (config, editor) => Boolean(config && editor?.selectedLocation),
    ),
    bedroomsControlKey: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      (locationType) => `${locationType}Bedrooms`,
    ),
    jobNotInZone: createSelector(selectJobInCurrentZone, (inZone) => !inZone),
    showUnitNumber: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      (locationType) => locationType !== 'dock',
    ),
    formattedLocationType: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      (locationType) => {

        return JOB_LOCATION_TYPES
          .find((lt) => lt.key === locationType)?.label || locationType;
      },
    ),
    actionsMenuItems: createSelector(
      selectActiveJobLocation,
      selectJobInCurrentZone,
      (jobLocation, jobInCurrentZone): MenuItem[] => [
        {
          label: 'Delete Location',
          icon: 'pi pi-trash',
          disabled: !jobInCurrentZone || !jobLocation,
          command: () => this.store.dispatch(JobLocationsOverviewActions.deleteLocationButtonClicked()),
        },
      ],
    ),
  }))

  ngAfterViewInit(): void {
    this._focusAutocompleteWhenFlagged();
    this._hideAddressAutocompleteMenuOnScroll();
    this._markDetailsAsTouchedAfterWizard();
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  private _focusAutocompleteWhenFlagged() {
    this._subs.sink = this.store.select(overviewLocationsFeature.selectShouldFocusAutocomplete).pipe(
      filter(Boolean),
      observeOn(animationFrameScheduler),
      tap(() => {
        const autocomplete = this.addressAutocomplete?.el?.nativeElement;

        if (!autocomplete) return;

        autocomplete.focus();

        if (autocomplete.value) {
          requestAnimationFrame(() => autocomplete.select());
        }

        this.store.dispatch(JobLocationsOverviewActions.autocompleteFocused());
      }),
    ).subscribe();
  }

  private _hideAddressAutocompleteMenuOnScroll() {
    this._subs.sink = this.freyaHelper.layoutScrolled.pipe(
      debounceTime(10),
      tap(() => {
        const pacContainers = document.querySelectorAll('.pac-container');

        pacContainers.forEach(container => {
          (container as HTMLElement).style.display = 'none';
        });
      }),
    ).subscribe();
  }

  private _markDetailsAsTouchedAfterWizard() {
    this._subs.sink = this.store.select(overviewLocationsFeature.selectShouldTouchDetailFields).pipe(
      filter(Boolean),
      observeOn(animationFrameScheduler),
      tap(() => {
        this.buildingTypeModel?.control?.markAsTouched();
        this.sizeModel?.control?.markAsTouched();
        this.accessModel?.control?.markAsTouched();
        this.store.dispatch(JobLocationsOverviewActions.detailFieldsTouched());
      }),
    ).subscribe();
  }
}
